// Keeps a playing video on the wall's timeline. Drift is actual minus expected position, in
// seconds (positive = ahead). Small drift is trimmed by nudging the playback rate, which is
// invisible; large drift (a tile that joined late or stalled) is fixed by seeking.

export const DEADBAND_S = 0.015; // under half a frame at 30 fps: leave it alone
const SEEK_S = 0.5;
const SEEK_ONLY_S = 0.25; // without rate control, every correction is a (visible) seek
const GAIN = 0.5; // 100 ms behind → 5 % faster, caught up in about 2 s
const MAX_RATE_DELTA = 0.05;

/** Drift folded into (-duration/2, duration/2], so a looping video near its wrap point isn't "a whole loop off". */
export const loopDrift = (actualS, expectedS, durationS) => {
  const drift = actualS - expectedS;
  if (!(durationS > 0)) return drift;
  const folded = ((drift % durationS) + durationS) % durationS;
  return folded > durationS / 2 ? folded - durationS : folded;
};

/**
 * What to do about `driftS`: `{ seekTo: true }` (jump to the expected position) or
 * `{ rate }` (play at this rate until the next check). `canSetRate` is false on players whose
 * playbackRate is unreliable; they only seek, and only when well off.
 */
export const driftAction = (driftS, { canSetRate = true } = {}) => {
  const size = Math.abs(driftS);
  if (!canSetRate) return size > SEEK_ONLY_S ? { seekTo: true } : { rate: 1 };
  if (size > SEEK_S) return { seekTo: true };
  if (size <= DEADBAND_S) return { rate: 1 };
  const delta = Math.min(size * GAIN, MAX_RATE_DELTA);
  return { rate: driftS > 0 ? 1 - delta : 1 + delta };
};
